import type { FileUploadState } from '../../../../components/inputs/FilesField';
import { ApiError } from '../../../../lib/apiError';
import { pressReleasesApi } from '../pressReleaseApi';
import { drawThumbnail } from './drawThumbnail';

/**
 * Files each picked file against a press release that now exists — ONE request per file, in order,
 * each with the thumbnail drawn of it. Routing's `fileAll`, for a press release.
 *
 * **After the release, never with it.** A file that fails does not undo the release — resubmitting
 * the form would write it twice. What failed is RETURNED, and the form names it; the writer
 * attaches it again from the release's own page.
 *
 * `onProgress` reports the real figure across the whole batch, because `apiUpload` measures it;
 * `onFile` reports each file's OWN state, for the row that shows it.
 */
export async function fileAttachments(
  releaseId: string,
  files: File[],
  onProgress: (percent: number) => void,
  onFile?: (index: number, state: FileUploadState) => void,
): Promise<{ index: number; name: string; reason: string }[]> {
  const failed: { index: number; name: string; reason: string }[] = [];

  for (const [index, file] of files.entries()) {
    try {
      onFile?.(index, { percent: 0 });
      const thumbnail = await drawThumbnail(file);

      await pressReleasesApi.attach(releaseId, file, thumbnail, (percent) => {
        onFile?.(index, { percent });
        onProgress(Math.floor(((index + percent / 100) / files.length) * 100));
      });
      onFile?.(index, { done: true });
    } catch (caught) {
      const reason = caught instanceof ApiError ? caught.message : 'It could not be filed.';

      onFile?.(index, { error: reason });
      failed.push({ index, name: file.name, reason });
    }
  }

  onProgress(100);

  return failed;
}
